import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Heart, Hand, Truck } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

export default function Home() {
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/products", { params: { featured: true } }).then((r) => setProducts(r.data));
  }, []);

  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-12 lg:pt-20 pb-20 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-6 relative z-10">
            <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] text-[#1F1C18]">
              L'art du <span className="italic text-terracotta">tricot et du crochet</span>,
              <br />
              <span className="font-accent text-mustard text-6xl lg:text-7xl -rotate-2 inline-block mt-2">fait avec passion</span>
            </h1>
            <p className="font-body text-base lg:text-lg text-[#6B655C] mt-8 max-w-xl leading-relaxed">
              Des vêtements et accessoires uniques, tricotés et crochetés à la main dans mon atelier, avec des matières certifiées OEKO-TEX douces et agréables à porter.
            </p>
            <div className="flex flex-wrap gap-4 mt-10">
              <Link to="/shop" className="btn-primary" data-testid="hero-shop-btn">
                Découvrir la boutique <ArrowRight size={18} />
              </Link>
              <Link to="/about" className="btn-secondary" data-testid="hero-about-btn">
                Mon histoire
              </Link>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="absolute -top-10 -right-10 w-64 h-64 bg-terracotta/15 rounded-full blur-3xl"></div>
            <div className="absolute -bottom-10 -left-10 w-72 h-72 bg-mustard/15 rounded-full blur-3xl"></div>
            <div className="relative grid grid-cols-5 gap-3">
              <div className="col-span-3 aspect-[3/4] rounded-3xl overflow-hidden shadow-xl">
                <img
                  src="https://customer-assets-agu9un31.emergentagent.net/job_artisan-crafts-23/artifacts/f9fa1d15986af1bf_Vitrine%20boh%C3%A8me%20de%20tricots%20artisanaux.webp"
                  alt="Vitrine bohème de tricots et crochets artisanaux"
                  className="w-full h-full object-cover"
                  data-testid="hero-main-image"
                />
              </div>
              <div className="col-span-2 space-y-3">
                <div className="aspect-square rounded-3xl overflow-hidden shadow-md">
                  <img
                    src="https://customer-assets-agu9un31.emergentagent.net/job_artisan-crafts-23/artifacts/54ce9dc2bf6ed663_Chat%20joueur%20et%20pelote%20arc-en-ciel.webp"
                    alt="Labounite : chat noir et pelote multicolore"
                    className="w-full h-full object-cover"
                    data-testid="hero-logo-image"
                  />
                </div>
                <div className="aspect-square rounded-3xl overflow-hidden shadow-md bg-patchwork flex items-center justify-center p-6">
                  <p className="font-accent text-3xl text-white text-center -rotate-3 leading-tight drop-shadow-md">
                    100% Fait Main ♡
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* VALUES */}
      <section className="border-y border-[#E5E1D8] bg-[#F7F5F0]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
          {[
            { icon: Hand, title: "Fait Main", text: "Chaque pièce est tricotée ou crochetée avec patience et passion" },
            { icon: Heart, title: "Vêtements Originaux", text: "Styles modernes, bohèmes, traditionnels" },
            { icon: Sparkles, title: "Artisanat Éthique", text: "Production locale et rémunération juste" },
            { icon: Truck, title: "Livraisons", text: "Envois gratuits en France et DOM-TOM" },
          ].map((v, i) => (
            <div key={i} className="flex items-start gap-4">
              <div className="p-3 rounded-2xl bg-terracotta/10">
                <v.icon className="text-terracotta" size={22} strokeWidth={1.5} />
              </div>
              <div>
                <h3 className="font-serif-display text-xl mb-1">{v.title}</h3>
                <p className="text-sm text-[#6B655C] leading-relaxed">{v.text}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED PRODUCTS */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-12">
          <div>
            <p className="font-accent text-3xl text-terracotta -rotate-2 mb-2">Coups de cœur</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight">Pièces sélectionnées<br/>par notre artisane</h2>
          </div>
          <Link to="/shop" className="text-sm uppercase tracking-[0.18em] text-moss hover:text-terracotta inline-flex items-center gap-2" data-testid="see-all-link">
            Tout voir <ArrowRight size={16} />
          </Link>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 lg:gap-8">
          {products.slice(0, 4).map((p, i) => (
            <ProductCard key={p.id} product={p} idx={i} />
          ))}
        </div>
      </section>

      {/* STORY BANNER */}
      <section className="bg-patchwork text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="aspect-[5/4] rounded-3xl overflow-hidden">
            <img
              src="https://customer-assets-agu9un31.emergentagent.net/job_artisan-crafts-23/artifacts/a0f14a4e72e4ed5d_Pelotes%20de%20laine%20color%C3%A9es%20en%20studio.webp"
              alt="Pelotes de laine colorées"
              className="w-full h-full object-cover"
            />
          </div>
          <div>
            <p className="font-accent text-3xl text-white -rotate-2 mb-3 drop-shadow">Mon Histoire</p>
            <h2 className="font-serif-display text-4xl sm:text-5xl leading-tight mb-6 drop-shadow-md">
              Un atelier, des mains, mille fils.
            </h2>
            <p className="font-body text-base text-white/95 leading-relaxed mb-8 max-w-xl drop-shadow-sm">
              Labounite, c'est l'aventure d'une artisane passionnée par le travail manuel de la laine et du coton dans mon atelier au cœur de la France, le Cantal.
            </p>
            <Link to="/about" className="btn-primary" data-testid="story-cta">
              Découvrir l'histoire <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
